/**
 * 606 Meta5 AI Trading - Trading Schools Tab (المدارس التداولية)
 *
 * Lets the user pick a pre-curated school/preset (SMC / ICT / Wyckoff /
 * Price Action / Trend Rider / Fast Scalping / Mean Reversion / Conservative).
 * Each card explains exactly HOW the bot uses that school.
 *
 * Applying a school updates `trading_bot_settings` server-side. The bot
 * picks up the new interval/strategies on the next loop cycle — no restart.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  GraduationCap, Loader2, CheckCircle2, Sparkles, Zap, Crosshair, Target,
  TrendingUp, Activity, Shield, BarChart3, AlertTriangle, ChevronDown,
  ChevronUp, Star, Clock, Coins, Layers,
} from 'lucide-react';
import { fetchSchools, applySchool, fetchSchoolDetail } from '../../lib/trading-api';
import { useTradingContext } from '../../context/TradingContext';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';

const ICON_MAP = {
  'crosshair': Crosshair,
  'target': Target,
  'trending-up': TrendingUp,
  'candlestick': BarChart3,
  'zap': Zap,
  'activity': Activity,
  'shield': Shield,
  'sparkles': Sparkles,
};

const BADGE_STYLES = {
  RECOMMENDED: { ar: 'موصى به ⭐', en: 'RECOMMENDED ⭐', color: '#fbbf24' },
  MOST_USED: { ar: 'الأكثر استخداماً', en: 'MOST USED',  color: '#d4af37' },
  PRECISE:   { ar: 'دقة عالية',         en: 'PRECISE',    color: '#a855f7' },
  SWING:     { ar: 'سوينج',             en: 'SWING',      color: '#3b82f6' },
  CLASSIC:   { ar: 'كلاسيكي',           en: 'CLASSIC',    color: '#10b981' },
  MOMENTUM:  { ar: 'زخم',               en: 'MOMENTUM',   color: '#f97316' },
  FAST:      { ar: 'سريع جداً',         en: 'FAST',       color: '#ef4444' },
  RANGE:     { ar: 'سوق عرضي',          en: 'RANGE',      color: '#06b6d4' },
  SAFE:      { ar: 'آمن',               en: 'SAFE',       color: '#71717a' },
};

const SchoolsTab = ({ language = 'ar' }) => {
  const isAr = language === 'ar';
  const { refreshSettings } = useTradingContext();
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [current, setCurrent] = useState(null);
  const [applyingId, setApplyingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmSchool, setConfirmSchool] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSchools();
      setSchools(data.schools || []);
      setCurrent(data.current || null);
    } catch (e) {
      toast.error(isAr ? 'فشل تحميل المدارس' : 'Failed to load schools');
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  const handleApply = async (school) => {
    setConfirmSchool(null);
    setApplyingId(school.id);
    try {
      const res = await applySchool(school.id);
      if (res.success) {
        toast.success(
          isAr
            ? `تم تطبيق مدرسة "${school.name_ar}" بنجاح`
            : `Applied "${school.name_en}" school`,
        );
        setCurrent(school.id);
        if (refreshSettings) await refreshSettings();
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        (isAr ? 'فشل تطبيق المدرسة' : 'Failed to apply school'),
      );
    } finally {
      setApplyingId(null);
    }
  };

  const openDetail = async (school) => {
    setDetail({ loading: true, school });
    setDetailLoading(true);
    try {
      const d = await fetchSchoolDetail(school.id);
      setDetail({ school, full: d });
    } catch (_) {
      setDetail(null);
      toast.error(isAr ? 'فشل تحميل التفاصيل' : 'Failed to load details');
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-10 w-10 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trading-schools-tab">

      {/* Header */}
      <div className="bg-gradient-to-br from-[#d4af37]/10 via-transparent to-purple-500/5 border border-[#d4af37]/20 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center shrink-0">
            <GraduationCap className="h-6 w-6 text-[#d4af37]" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-white">
              {isAr ? 'المدارس التداولية' : 'Trading Schools'}
            </h2>
            <p className="text-sm text-[#A1A1AA] mt-1">
              {isAr
                ? 'كل مدرسة هي إعداد جاهز يضم: الاستراتيجيات + المخاطر + الإطار الزمني + كيف يستخدم البوت هذه المدرسة. اضغط على مدرسة لتطبيقها بضغطة واحدة.'
                : 'Each school is a curated preset: strategies + risk + timeframe + how the bot uses it. Click to apply in one tap.'}
            </p>
            {current && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-bold">
                <CheckCircle2 className="h-3 w-3" />
                {isAr ? 'المدرسة المفعّلة حالياً:' : 'Currently active:'} {schools.find(s => s.id === current)?.[isAr ? 'name_ar' : 'name_en'] || current}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Schools Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="schools-grid">
        {schools.map((school, idx) => (
          <SchoolCard
            key={school.id}
            school={school}
            isAr={isAr}
            isActive={current === school.id}
            isApplying={applyingId === school.id}
            isExpanded={expandedId === school.id}
            onToggleExpand={() => setExpandedId(expandedId === school.id ? null : school.id)}
            onApply={() => setConfirmSchool(school)}
            onShowDetails={() => openDetail(school)}
            delay={idx * 0.04}
          />
        ))}
      </div>

      {/* Confirm Apply Dialog */}
      <Dialog open={!!confirmSchool} onOpenChange={(o) => { if (!o) setConfirmSchool(null); }}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg" data-testid="schools-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#d4af37]" />
              {isAr ? 'تأكيد تطبيق المدرسة' : 'Confirm Apply School'}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA] mt-2">
              {confirmSchool && (
                <>
                  {isAr
                    ? `سيتم تطبيق إعدادات مدرسة "${confirmSchool.name_ar}" فوراً على البوت. ستحل محل الإعدادات الحالية (الاستراتيجيات، الإطار الزمني، المخاطر، حلقة الفحص).`
                    : `Settings of "${confirmSchool.name_en}" will replace current bot settings (strategies, timeframe, risk, loop cadence) immediately.`}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {confirmSchool && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-[#d4af37]">
                <Star className="h-4 w-4" />
                <span className="font-bold">{isAr ? confirmSchool.name_ar : confirmSchool.name_en}</span>
              </div>
              <p className="text-[#A1A1AA] text-xs">
                {isAr ? confirmSchool.tagline_ar : confirmSchool.tagline_en}
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmSchool(null)}
              className="border-white/10 text-white hover:bg-white/5"
              data-testid="schools-confirm-cancel"
            >
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={() => handleApply(confirmSchool)}
              className="bg-[#d4af37] hover:bg-[#c19b2e] text-black font-bold gap-2"
              data-testid="schools-confirm-apply"
            >
              <Sparkles className="h-4 w-4" />
              {isAr ? 'تطبيق' : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog (raw preset) */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-[#d4af37]" />
              {isAr ? 'تفاصيل المدرسة' : 'School Details'}
            </DialogTitle>
          </DialogHeader>
          {detailLoading || !detail?.full ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-[#d4af37]" />
            </div>
          ) : (
            <DetailContent full={detail.full} isAr={isAr} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// School Card
// ============================================================
function SchoolCard({ school, isAr, isActive, isApplying, isExpanded, onToggleExpand, onApply, onShowDetails, delay }) {
  const Icon = ICON_MAP[school.icon] || GraduationCap;
  const Badge = BADGE_STYLES[school.badge] || {};
  const c = school.color;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white/[0.02] backdrop-blur-xl border rounded-2xl overflow-hidden transition-colors group"
      style={{
        borderColor: isActive ? c : 'rgba(255,255,255,0.10)',
        boxShadow: isActive ? `0 0 0 1px ${c}40, 0 4px 24px ${c}20` : 'none',
      }}
      data-testid={`school-card-${school.id}`}
    >
      {/* Top */}
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center border shrink-0"
            style={{ backgroundColor: `${c}15`, borderColor: `${c}40` }}
          >
            <Icon className="h-5 w-5" style={{ color: c }} />
          </div>
          {school.badge && (
            <span
              className="px-2 py-0.5 rounded-md text-[9px] font-bold border whitespace-nowrap"
              style={{ color: Badge.color || c, backgroundColor: `${Badge.color || c}15`, borderColor: `${Badge.color || c}40` }}
            >
              {isAr ? (Badge.ar || school.badge) : (Badge.en || school.badge)}
            </span>
          )}
        </div>

        <div>
          <h3 className="text-white font-bold text-base leading-tight">
            {isAr ? school.name_ar : school.name_en}
          </h3>
          <p className="text-xs mt-1" style={{ color: `${c}cc` }}>
            {isAr ? school.tagline_ar : school.tagline_en}
          </p>
        </div>

        <p className="text-xs text-[#A1A1AA] line-clamp-3 leading-relaxed">
          {isAr ? school.description_ar : school.description_en}
        </p>

        {/* Recommended symbols */}
        <div className="flex flex-wrap gap-1">
          {(school.recommended_symbols || []).map((sym) => (
            <span
              key={sym}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono bg-white/5 border border-white/10 text-[#A1A1AA]"
            >
              <Coins className="h-2.5 w-2.5" />
              {sym}
            </span>
          ))}
        </div>
      </div>

      {/* How bot uses it (collapsible) */}
      <button
        onClick={onToggleExpand}
        className="w-full px-4 py-2 flex items-center justify-between text-xs font-bold border-t border-white/10 hover:bg-white/5 transition-colors"
        style={{ color: c }}
        data-testid={`school-expand-${school.id}`}
      >
        <span className="flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" />
          {isAr ? `كيف يستخدمها البوت (${school.how_bot_uses_it.length} نقاط)` : `How the bot uses it (${school.how_bot_uses_it.length} points)`}
        </span>
        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-white/10 bg-black/40"
          >
            <ul className="p-4 space-y-2 text-xs text-[#D4D4D8] leading-relaxed">
              {school.how_bot_uses_it.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#d4af37] shrink-0 font-bold">{i + 1}.</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer actions */}
      <div className="p-3 border-t border-white/10 flex gap-2">
        <Button
          onClick={onShowDetails}
          variant="outline"
          size="sm"
          className="flex-1 border-white/10 text-white hover:bg-white/5 text-xs"
          data-testid={`school-details-${school.id}`}
        >
          {isAr ? 'الإعدادات' : 'Settings'}
        </Button>
        <Button
          onClick={onApply}
          disabled={isApplying || isActive}
          size="sm"
          className="flex-1 text-xs font-bold gap-1 disabled:opacity-50"
          style={{
            backgroundColor: isActive ? '#10b981' : c,
            color: isActive ? '#fff' : (c === '#d4af37' ? '#000' : '#fff'),
          }}
          data-testid={`school-apply-${school.id}`}
        >
          {isApplying ? (
            <><Loader2 className="h-3 w-3 animate-spin" /> {isAr ? 'يطبق…' : 'Applying…'}</>
          ) : isActive ? (
            <><CheckCircle2 className="h-3 w-3" /> {isAr ? 'مفعّلة' : 'Active'}</>
          ) : (
            <><Sparkles className="h-3 w-3" /> {isAr ? 'تطبيق' : 'Apply'}</>
          )}
        </Button>
      </div>
    </motion.div>
  );
}

// ============================================================
// Detail Dialog Content
// ============================================================
function DetailContent({ full, isAr }) {
  const d = full.defaults || {};
  const rows = [
    { label: isAr ? 'الاستراتيجيات النشطة' : 'Active Strategies', value: (d.active_strategies || []).join(', ') },
    { label: isAr ? 'النمط' : 'Mode',              value: d.strategy_mode },
    { label: isAr ? 'الإطار الزمني' : 'Timeframe',  value: d.timeframe },
    { label: isAr ? 'الحد الأدنى للثقة' : 'Min Confidence', value: d.smc_min_confidence != null ? `${(d.smc_min_confidence * 100).toFixed(0)}%` : '—' },
    { label: isAr ? 'مخاطرة لكل صفقة' : 'Risk per Trade', value: d.risk_per_trade_pct != null ? `${d.risk_per_trade_pct}%` : '—' },
    { label: isAr ? 'نسبة المخاطرة/المكافأة' : 'Risk:Reward', value: d.risk_reward_ratio != null ? `1:${d.risk_reward_ratio}` : '—' },
    { label: isAr ? 'حد الصفقات المفتوحة' : 'Max Open Positions', value: d.max_open_positions },
    { label: isAr ? 'الحد اليومي للخسارة' : 'Daily Loss Limit',  value: d.daily_loss_limit_pct != null ? `${d.daily_loss_limit_pct}%` : '—' },
    { label: isAr ? 'الحد الشهري للخسارة' : 'Monthly Loss Limit',value: d.monthly_loss_limit_pct != null ? `${d.monthly_loss_limit_pct}%` : '—' },
    { label: isAr ? 'دورة الفحص' : 'Loop Interval', value: d.loop_interval_seconds != null ? `${d.loop_interval_seconds}s` : '—' },
    { label: isAr ? 'EMA السريع' : 'EMA Fast', value: d.ema_fast },
    { label: isAr ? 'EMA البطيء' : 'EMA Slow', value: d.ema_slow },
    { label: isAr ? 'فترة RSI' : 'RSI Period', value: d.rsi_period },
    { label: isAr ? 'موافقة AI' : 'AI Confirmation', value: d.use_ai_confirmation ? (isAr ? 'مفعّل' : 'ON') : (isAr ? 'معطّل' : 'OFF') },
    { label: isAr ? 'فلتر الأخبار' : 'News Filter', value: d.use_news_filter ? (isAr ? 'مفعّل' : 'ON') : (isAr ? 'معطّل' : 'OFF') },
  ];

  return (
    <div className="space-y-4 mt-2" data-testid="school-detail-content">
      <div>
        <h3 className="text-base font-bold text-white">{isAr ? full.name_ar : full.name_en}</h3>
        <p className="text-xs text-[#A1A1AA] mt-1">{isAr ? full.description_ar : full.description_en}</p>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        {rows.map((r) => (
          <div key={r.label} className="bg-white/5 border border-white/10 rounded-lg p-2 flex flex-col">
            <span className="text-[10px] text-[#71717a] uppercase tracking-wide">{r.label}</span>
            <span className="text-white font-mono mt-0.5">{r.value ?? '—'}</span>
          </div>
        ))}
      </div>

      <div>
        <h4 className="text-xs font-bold text-[#d4af37] mb-2 flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" />
          {isAr ? 'كيف يستخدمها البوت' : 'How the bot uses it'}
        </h4>
        <ol className="space-y-1.5 text-xs text-[#D4D4D8] list-decimal list-inside">
          {full.how_bot_uses_it.map((b, i) => <li key={i}>{b}</li>)}
        </ol>
      </div>

      <div>
        <h4 className="text-xs font-bold text-[#d4af37] mb-2 flex items-center gap-1.5">
          <Coins className="h-3 w-3" />
          {isAr ? 'الرموز الموصى بها' : 'Recommended symbols'}
        </h4>
        <div className="flex flex-wrap gap-1.5">
          {(full.recommended_symbols || []).map((sym) => (
            <span key={sym} className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-xs font-mono text-white">
              {sym}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SchoolsTab;
